<script setup>
import Button from 'dashboard/components-next/button/Button.vue';
import ButtonGroup from 'dashboard/components-next/buttonGroup/ButtonGroup.vue';
import { useUISettings } from 'dashboard/composables/useUISettings';
import { computed, onMounted } from 'vue';
import { FEATURE_FLAGS } from 'dashboard/featureFlags';
import {
  useFunctionGetter,
  useMapGetter,
  useStore,
} from 'dashboard/composables/store';
import { useKeyboardEvents } from 'dashboard/composables/useKeyboardEvents';

const { updateUISettings } = useUISettings();
const store = useStore();

const currentAccountId = useMapGetter('getCurrentAccountId');
const isFeatureEnabledonAccount = useMapGetter(
  'accounts/isFeatureEnabledonAccount'
);

const showCopilotTab = computed(() =>
  isFeatureEnabledonAccount.value(currentAccountId.value, FEATURE_FLAGS.CAPTAIN)
);

const olistIntegration = useFunctionGetter(
  'integrations/getIntegration',
  'olist'
);
const productCatalogIntegration = useFunctionGetter(
  'integrations/getIntegration',
  'product_catalog'
);

const showOlistTab = computed(() => olistIntegration.value?.enabled || false);
const showProductCatalogTab = computed(
  () => productCatalogIntegration.value?.enabled || showOlistTab.value || false
);

const { uiSettings } = useUISettings();
const isContactSidebarOpen = computed(
  () => uiSettings.value.is_contact_sidebar_open
);
const isCopilotPanelOpen = computed(
  () => uiSettings.value.is_copilot_panel_open
);
const isProductCatalogPanelOpen = computed(
  () => uiSettings.value.is_product_catalog_panel_open
);

const toggleConversationSidebarToggle = () => {
  updateUISettings({
    is_contact_sidebar_open: !isContactSidebarOpen.value,
    is_copilot_panel_open: false,
    is_olist_panel_open: false,
    is_product_catalog_panel_open: false,
  });
};

const handleConversationSidebarToggle = () => {
  updateUISettings({
    is_contact_sidebar_open: true,
    is_copilot_panel_open: false,
    is_olist_panel_open: false,
    is_product_catalog_panel_open: false,
  });
};

const handleCopilotSidebarToggle = () => {
  updateUISettings({
    is_contact_sidebar_open: false,
    is_copilot_panel_open: true,
    is_olist_panel_open: false,
    is_product_catalog_panel_open: false,
  });
};

const handleProductCatalogToggle = () => {
  updateUISettings({
    is_contact_sidebar_open: false,
    is_copilot_panel_open: false,
    is_olist_panel_open: false,
    is_product_catalog_panel_open: true,
  });
};

const keyboardEvents = {
  'Alt+KeyO': {
    action: toggleConversationSidebarToggle,
  },
};
useKeyboardEvents(keyboardEvents);

onMounted(() => {
  store.dispatch('integrations/get');
});
</script>

<template>
  <ButtonGroup
    class="absolute top-[84px] z-[95] flex flex-col items-center justify-center gap-1.5 rounded-full border border-n-weak bg-white/90 p-1.5 shadow-sm backdrop-blur-xl transition-shadow duration-200 hover:shadow dark:bg-n-solid-2/90 ltr:right-3 rtl:left-3"
  >
    <Button
      v-tooltip.top="$t('CONVERSATION.SIDEBAR.CONTACT')"
      ghost
      slate
      sm
      class="!rounded-full transition-all duration-[250ms] ease-out hover:!bg-n-alpha-2 dark:hover:!bg-n-alpha-2 active:!scale-95 active:!brightness-105 active:duration-75"
      :class="{
        '!bg-n-alpha-2 active:shadow-sm': isContactSidebarOpen,
      }"
      icon="i-ph-user-bold"
      @click="handleConversationSidebarToggle"
    />
    <Button
      v-if="showCopilotTab"
      v-tooltip.bottom="$t('CONVERSATION.SIDEBAR.COPILOT')"
      ghost
      slate
      sm
      class="!rounded-full transition-all duration-[250ms] ease-out hover:!bg-n-alpha-2 dark:hover:!bg-n-alpha-2 active:!scale-95 active:duration-75"
      :class="{
        '!bg-n-alpha-2 !text-n-iris-9 active:!brightness-105 active:shadow-sm':
          isCopilotPanelOpen,
      }"
      icon="i-woot-captain"
      @click="handleCopilotSidebarToggle"
    />
    <Button
      v-if="showProductCatalogTab"
      v-tooltip.bottom="$t('CONVERSATION.SIDEBAR.CATALOG')"
      ghost
      sm
      class="!rounded-full !bg-n-blue-9 !text-white transition-all duration-[250ms] ease-out hover:!bg-n-blue-10 dark:hover:!bg-n-blue-8 active:!scale-95 active:!brightness-105 active:duration-75"
      :class="{
        'ring-2 ring-n-blue-4 shadow-sm': isProductCatalogPanelOpen,
      }"
      icon="i-lucide-shopping-cart"
      @click="handleProductCatalogToggle"
    />
  </ButtonGroup>
</template>
